import type { AIComponentAction } from "schemas/generated/ai";

import * as React from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import {
  scrollToComponentId,
  setCanvasInteractionMode,
} from "@v1/components/canvas/canvas-reducer";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import {
  handleStreamingAction,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  setEditorMode,
  setStreamingUpdate,
} from "@v1/reducers/core-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { Play } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

export const JSONActionPlayer: React.FC = () => {
  const dispatch = useEditorDispatch();
  const [jsonInput, setJsonInput] = React.useState("");
  const [isPlaying, setIsPlaying] = React.useState(false);
  const activeCanvas = useEditorSelector((state) => state.canvas.activeCanvas);

  const store = useEditorStore();

  const handlePlay = async () => {
    if (!jsonInput.trim() || isPlaying) {
      return;
    }

    const draftElement =
      selectDraftElement_warningThisWillRerenderOnEveryUpdate(store.getState());

    setIsPlaying(true);

    try {
      const actions: AIComponentAction[] = JSON.parse(jsonInput);
      const streamingUpdateId = uuidv4();
      const draftElementComponent = draftElement?.component;

      if (!draftElementComponent) {
        return;
      }

      // Create a dummy component just to initialize the streaming state
      dispatch(
        setStreamingUpdate({
          id: streamingUpdateId,
          draftElementComponent: draftElementComponent,
          isStreaming: false,
          actions: [],
          originalStyles: {},
          textMap: {},
          repaintKey: 0,
        }),
      );
      dispatch(setEditorMode("aiGeneration"));

      // Process each action with a small delay to simulate streaming
      for (const action of actions) {
        dispatch(
          // @ts-ignore
          handleStreamingAction({
            ...action,
            activeCanvas,
          }),
        );

        if (action.componentId) {
          dispatch(scrollToComponentId({ componentId: action.componentId }));
        }

        // Add a small delay between actions
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error("Failed to parse or play JSON:", error);
      dispatch(setStreamingUpdate(null));
      dispatch(setEditorMode("edit"));
      dispatch(setCanvasInteractionMode("edit"));
    } finally {
      setIsPlaying(false);
    }
  };

  useReploHotkeys({
    enter: () => void handlePlay(),
  });

  return (
    <div className="flex h-[calc(100%-25px)] flex-col">
      <div className="flex-1 space-y-2 border-t p-4">
        <DeprecatedTextarea
          value={jsonInput}
          onChange={setJsonInput}
          placeholder={`Paste your JSON actions here... Example:
[{
  "type": "setStyles",
  "componentId": "your-component-id",
  "value": {
    "backgroundColor": "#1A1A1A"
  }
}]`}
          onEnter={() => void handlePlay()}
          size="base"
          layoutClassName="h-full min-h-[200px] w-full"
          isDisabled={isPlaying}
        />

        <div className="flex justify-end">
          <DeprecatedButton
            variant="primary"
            size="sm"
            onClick={() => void handlePlay()}
            disabled={!jsonInput.trim() || isPlaying}
          >
            <div className="flex items-center gap-2">
              Play Actions
              {isPlaying ? (
                <Spinner className="size-2.5" variant="white" />
              ) : (
                <Play size={14} />
              )}
            </div>
          </DeprecatedButton>
        </div>
      </div>
    </div>
  );
};
